import { HistoryChange } from "../HistoryActionModel";
import { formatDateIfValid } from "./dateHelpers";
import { InformTableDataRow } from "../Components/InformTable";

const DATE_PROPERTIES = [
    "CreateDate",
    "ModifyDate",
    "ExpirationDate",
    "NextRebillDate",
    "LastRebillDate",
];

const PROPERTY_LABELS: Record<string, string> = {
    CreateDate: "Create Date",
    ModifyDate: "Modify Date",
    ExpirationDate: "Expiration Date",
    NextRebillDate: "Next Rebill Date",
    LastRebillDate: "Last Rebill Date",
    isActive: "Active",
    GracePeriod: "Grace Period",
    IsFree: "Is Free",
    IsTrial: "Is Trial",
    HasLifeTime: "Has Lifetime",
};

export const getPropertyLabel = (property: string): string =>
    PROPERTY_LABELS[property] ?? property;

export const isDateProperty = (property: string): boolean =>
    DATE_PROPERTIES.includes(property);

// Build InformTable rows for Added (NewValue) or Deleted (OldValue) — excludes date properties
export const buildValuesTableData = (
    changes: HistoryChange[],
    mode: "new" | "old",
    format: (property: string, value: any) => string | null,
    exclude: string[] = []
): InformTableDataRow[] => {
    return changes
        .filter(c => !exclude.includes(c.Property) && !isDateProperty(c.Property))
        .flatMap(c => {
            const raw = mode === "new" ? c.NewValue : c.OldValue;
            const formatted = format(c.Property, raw);
            return formatted !== null ? [{ property: getPropertyLabel(c.Property), value: formatted }] : [];
        });
};

// Build date section rows from Changes
export const buildDateTableData = (
    changes: HistoryChange[],
    mode: "new" | "old"
): InformTableDataRow[] => {
    return changes
        .filter(c => isDateProperty(c.Property))
        .flatMap(c => {
            const raw = mode === "new" ? c.NewValue : c.OldValue;
            const formatted = formatDateIfValid(raw);
            return formatted ? [{ property: getPropertyLabel(c.Property), value: formatted }] : [];
        });
};

// Build Old Version / New Version tables for Modified — only shows actually changed fields
export const buildModifiedTableData = (
    changes: HistoryChange[],
    format: (property: string, value: any) => string | null,
    exclude: string[] = []
): { dataOld: InformTableDataRow[]; dataNew: InformTableDataRow[] } => {
    const relevant = changes.filter(
        c => !exclude.includes(c.Property) && c.OldValue !== c.NewValue
    );
    const dataOld = relevant.map(c => ({
        property: getPropertyLabel(c.Property),
        value: format(c.Property, c.OldValue) ?? "—",
    }));
    const dataNew = relevant.map(c => ({
        property: getPropertyLabel(c.Property),
        value: format(c.Property, c.NewValue) ?? "—",
    }));
    return { dataOld, dataNew };
};
